import { z } from 'zod';

/**
 * Central freshness client (replit.md §0.7). The green/amber/red status is
 * computed once in the database (`pgfin.sync_freshness`) and served by
 * `GET /api/freshness`; this module fetches + Zod-validates that payload (Zod at
 * the boundary — external data is never trusted) and maps the wire shape to a
 * camelCase record. No view re-derives staleness — they all read from here.
 */
export const FRESHNESS_STATUSES = ['green', 'amber', 'red'] as const;
export type FreshnessStatus = (typeof FRESHNESS_STATUSES)[number];

const wireSchema = z.object({
  entity: z.string(),
  freshness: z.enum(FRESHNESS_STATUSES),
  status: z.string(),
  scope: z.string().nullable(),
  last_synced_at: z.string().nullable(),
  age_seconds: z.number().nullable(),
  staleness_threshold_seconds: z.number(),
  last_error: z.string().nullable(),
});

const responseSchema = z.object({ entities: z.array(wireSchema) });

export interface FreshnessRecord {
  entity: string;
  freshness: FreshnessStatus;
  status: string;
  scope: string | null;
  lastSyncedAt: string | null;
  ageSeconds: number | null;
  stalenessThresholdSeconds: number;
  lastError: string | null;
}

export interface FetchFreshnessOptions {
  /** API origin; defaults to same-origin (relative URL — works across environments). */
  baseUrl?: string;
  /** Supabase access token; attached as a Bearer header when present. */
  accessToken?: string;
  /** Injectable fetch for tests. */
  fetchImpl?: typeof fetch;
}

export async function fetchFreshness(options: FetchFreshnessOptions = {}): Promise<FreshnessRecord[]> {
  const doFetch = options.fetchImpl ?? fetch;
  const headers: Record<string, string> = {};
  if (options.accessToken) {
    headers.Authorization = `Bearer ${options.accessToken}`;
  }

  const res = await doFetch(`${options.baseUrl ?? ''}/api/freshness`, { headers });
  if (!res.ok) {
    throw new Error(`Freshness request failed: ${res.status}`);
  }

  const parsed = responseSchema.parse(await res.json());
  return parsed.entities.map((e) => ({
    entity: e.entity,
    freshness: e.freshness,
    status: e.status,
    scope: e.scope,
    lastSyncedAt: e.last_synced_at,
    ageSeconds: e.age_seconds,
    stalenessThresholdSeconds: e.staleness_threshold_seconds,
    lastError: e.last_error,
  }));
}

/** Pick a single entity's freshness from the shared payload. */
export function selectFreshness(records: FreshnessRecord[], entity: string): FreshnessRecord | undefined {
  return records.find((r) => r.entity === entity);
}

/** Human (Estonian) display name for a sync source/entity. */
const SOURCE_NAMES: Record<string, string> = {
  smartaccounts: 'SmartAccounts',
  erply: 'Erply',
  pipedrive: 'Pipedrive',
  lhv: 'LHV',
  manual: 'Käsitsi',
  loan: 'Laenud',
  tax: 'Maksud',
  cash_flow: 'Rahavoog',
  forecast: 'Prognoosid',
};

export function sourceDisplayName(entity: string): string {
  return SOURCE_NAMES[entity] ?? entity.charAt(0).toUpperCase() + entity.slice(1);
}

/**
 * A source's age as a short Estonian relative string. A never-synced source
 * (no age) reads "sünkroon ootel" (sync pending), matching the canvas.
 */
export function formatFreshnessAge(record: FreshnessRecord): string {
  if (record.ageSeconds === null) return 'sünkroon ootel';
  const s = record.ageSeconds;
  if (s < 60) return 'äsja';
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86_400) return `${Math.round(s / 3600)} h`;
  return `${Math.round(s / 86_400)} p`;
}

/**
 * The full per-source badge label, e.g. "SmartAccounts · 2 h". LHV is the live
 * bank anchor, so it always reads "LHV · reaalajas" (real-time).
 */
export function freshnessSourceLabel(record: FreshnessRecord): string {
  const name = sourceDisplayName(record.entity);
  if (record.entity === 'lhv') return `${name} · reaalajas`;
  return `${name} · ${formatFreshnessAge(record)}`;
}
